import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { trackEvent } from "@/utils/analytics";

// Slim, dismissible "get the app" bar. Shown ONLY to Android mobile-web users (they can actually
// install), once per user (localStorage), and only until CAMPAIGN_END. Persists until tapped or
// closed — no auto-dismiss — so it's actually actionable. GA4-tracked (impression / click / dismiss).
const PLAY_URL = "https://play.google.com/store/apps/details?id=com.findgambia.app";
const STORAGE_KEY = "fg_app_banner_dismissed"; // stores "installed" (permanent) or a dismiss timestamp
const CAMPAIGN_END = new Date("2026-09-08T23:59:59"); // ~4 weeks; bar stops showing after this
const SHOW_DELAY_MS = 1500; // let the page settle first
const RESHOW_AFTER_MS = 7 * 24 * 60 * 60 * 1000; // re-show a dismissed banner after 7 days

// Whether the banner should stay hidden based on the stored value.
function suppressed() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (!v) return false;
    if (v === "installed") return true;          // tapped Install — don't nag (likely installed)
    const ts = Number(v);                          // dismissed at this time → re-show after 7 days
    return !Number.isNaN(ts) && Date.now() - ts < RESHOW_AFTER_MS;
  } catch {
    return false; // storage blocked — just show this session
  }
}

function isAndroidMobile() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /Android/i.test(ua) && /Mobile/i.test(ua);
}

export default function AppInstallBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (new Date() > CAMPAIGN_END) return;
    if (!isAndroidMobile()) return;
    if (suppressed()) return;
    const t = setTimeout(() => {
      setVisible(true);
      trackEvent("app_banner_impression", { platform: "android_web" });
    }, SHOW_DELAY_MS);
    return () => clearTimeout(t);
  }, []);

  const dismiss = (reason) => {
    // Store the dismiss time → the banner re-appears after RESHOW_AFTER_MS (7 days).
    try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch { /* ignore */ }
    setVisible(false);
    if (reason) trackEvent("app_banner_dismiss", { reason });
  };

  const onInstall = () => {
    trackEvent("app_banner_click", { platform: "android_web" });
    // Tapped Install → suppress permanently (they likely installed; don't nag).
    try { localStorage.setItem(STORAGE_KEY, "installed"); } catch { /* ignore */ }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 inset-x-0 z-50 bg-foreground text-background shadow-[0_-2px_12px_rgba(0,0,0,0.15)]">
      <div className="max-w-3xl mx-auto flex items-center gap-3 px-4 py-2.5">
        <img src="/icons/icon-192.png" alt="" className="h-9 w-9 rounded-lg flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold leading-tight">Get the FindGambia app</p>
          <p className="text-xs opacity-80 leading-tight">Faster browsing, deals &amp; updates on your phone.</p>
        </div>
        <a
          href={PLAY_URL}
          target="_blank"
          rel="noopener noreferrer"
          onClick={onInstall}
          className="flex-shrink-0 rounded-lg bg-background text-foreground text-sm font-semibold px-4 py-1.5 hover:opacity-90 transition-opacity"
        >
          Install
        </a>
        <button
          onClick={() => dismiss("close")}
          aria-label="Dismiss"
          className="flex-shrink-0 p-1 opacity-70 hover:opacity-100 transition-opacity"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
